import { useState, useEffect, useCallback, useMemo } from 'react'
import { biApi } from '../api/bi'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtMonto, fmtNum, presetRangoBi as presetRango, PRESETS_BI as PRESETS } from '../utils/bi'

const SEP = ''

// ─── Chip arrastrable (dimensión o medida) ───────────────────────────
function Chip({ item, tipo, onRemove, activa }) {
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify({ key: item.key, tipo }))}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', cursor: 'grab',
        background: activa ? colors.linkBg : colors.bgSoft, border: `1px solid ${activa ? colors.link : colors.border}`,
        borderRadius: radius.sm, fontSize: text.sec, color: activa ? colors.link : colors.text, fontFamily: font.sans, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: colors.textMeta }}>⠿</span>{item.label}
      {onRemove && <span onClick={onRemove} style={{ cursor: 'pointer', color: colors.textMeta, fontWeight: 700, marginLeft: 2 }}>×</span>}
    </div>
  )
}

// ─── Zona de drop (Filas / Columnas / Valores) ───────────────────────
function Zona({ titulo, hint, children, onDrop }) {
  const [over, setOver] = useState(false)
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ ...sx.cardTitle, marginBottom: 4 }}>{titulo}</div>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e) }}
        style={{
          minHeight: 44, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: 8,
          border: `1.5px dashed ${over ? colors.link : colors.border}`, borderRadius: radius.md,
          background: over ? colors.linkBg : colors.bg, transition: 'all .12s',
        }}
      >
        {children && children.length ? children : <span style={{ fontSize: text.meta, color: colors.textMeta }}>{hint}</span>}
      </div>
    </div>
  )
}

export default function BiCuboPage() {
  const [opts, setOpts] = useState({ dimensiones: [], medidas: [], ajustes: [] })
  const [preset, setPreset] = useState('todo')
  const [[desde, hasta], setRango] = useState(presetRango('todo'))
  const [filas, setFilas] = useState(['rubro'])
  const [columnas, setColumnas] = useState(['anio'])
  const [medida, setMedida] = useState('facturacion')
  const [ajuste, setAjuste] = useState('nominal')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { biApi.cuboOpciones().then(({ data }) => setOpts(data)).catch(() => {}) }, [])

  const cargar = useCallback(() => {
    setLoading(true)
    biApi.cubo({ desde, hasta, filas, columnas, medida, ajuste })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [desde, hasta, filas, columnas, medida, ajuste])
  useEffect(() => { cargar() }, [cargar])

  // Mover una dimensión a un destino (filas | columnas | campos), sacándola de donde esté.
  const mover = (key, destino) => {
    const nf = filas.filter((k) => k !== key)
    const nc = columnas.filter((k) => k !== key)
    if (destino === 'filas') nf.push(key)
    if (destino === 'columnas') nc.push(key)
    setFilas(nf); setColumnas(nc)
  }
  const dropEn = (destino) => (e) => {
    const d = JSON.parse(e.dataTransfer.getData('application/json') || '{}')
    if (!d.key) return
    if (destino === 'valores') { if (d.tipo === 'medida') setMedida(d.key); return }
    if (d.tipo === 'dim') mover(d.key, destino)
  }

  const aplicarPreset = (k) => { setPreset(k); setRango(presetRango(k)) }
  const usados = new Set([...filas, ...columnas])
  const dimsLibres = opts.dimensiones.filter((d) => !usados.has(d.key))
  const dimLabel = (k) => opts.dimensiones.find((d) => d.key === k)?.label || k
  const medLabel = (k) => opts.medidas.find((m) => m.key === k)?.label || k

  const esComprob = medida === 'comprobantes'
  const fmt = (v) => (esComprob ? fmtNum(v) : fmtMonto(v))
  const cols = data?.columnas || []
  const nf = data?.meta?.filas?.length || 0
  const nc = data?.meta?.columnas?.length || 0

  const maxCelda = useMemo(() => {
    if (!data) return 1
    let m = 0
    for (const f of data.filas) for (const c of Object.values(f.celdas)) m = Math.max(m, c)
    return m || 1
  }, [data])
  const shade = (v) => (v > 0 ? `rgba(29,78,216,${(v / maxCelda) * 0.14})` : 'transparent')

  // Grupos del encabezado de columnas en el nivel d (para colSpan anidado)
  const gruposNivel = (d) => {
    const g = []
    let cur = null
    for (const c of cols) {
      const pref = c.vals.slice(0, d + 1).join(SEP)
      if (cur && cur.pref === pref) cur.span++
      else { cur = { pref, label: c.vals[d], span: 1 }; g.push(cur) }
    }
    return g
  }

  const st = useMemo(() => ({
    input: { padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, fontFamily: font.sans, color: colors.text, background: colors.bg },
    presetBtn: (on) => ({ ...sx.pill, ...(on ? sx.pillActive : {}) }),
    th: { textAlign: 'right', padding: '7px 10px', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${colors.border}`, whiteSpace: 'nowrap', background: colors.bg },
    thc: { textAlign: 'center', padding: '5px 10px', fontSize: text.meta, fontWeight: 600, color: colors.textSec, borderBottom: `1px solid ${colors.borderSoft}`, borderLeft: `1px solid ${colors.borderSoft}`, whiteSpace: 'nowrap', background: colors.bg },
    td: { textAlign: 'right', padding: '6px 10px', fontSize: text.body, color: colors.text, ...sx.mono, borderBottom: `1px solid ${colors.borderSoft}`, whiteSpace: 'nowrap' },
    tdh: { textAlign: 'left', padding: '6px 10px', fontSize: text.body, fontFamily: font.sans, color: colors.text, borderBottom: `1px solid ${colors.borderSoft}`, whiteSpace: 'nowrap', background: colors.bg },
  }), [])

  return (
    <div style={{ padding: space['2xl'], maxWidth: 1320, margin: '0 auto', fontFamily: font.sans }}>
      <div style={{ marginBottom: space.lg }}>
        <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Inteligencia comercial</div>
        <h1 style={{ margin: '2px 0 0', fontSize: text.title + 6, color: colors.text }}>Cubo de ventas</h1>
      </div>

      {/* Campos disponibles para arrastrar */}
      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.md, marginBottom: space.md }}
        onDragOver={(e) => e.preventDefault()} onDrop={dropEn('campos')}>
        <div style={{ ...sx.cardTitle, marginBottom: 6 }}>Campos — arrastrá a Filas, Columnas o Valores</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {dimsLibres.map((d) => <Chip key={d.key} item={d} tipo="dim" />)}
          <span style={{ width: 1, height: 22, background: colors.border, margin: '0 4px' }} />
          {opts.medidas.map((m) => <Chip key={m.key} item={m} tipo="medida" activa={m.key === medida} />)}
        </div>
      </div>

      {/* Zonas */}
      <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap', marginBottom: space.md }}>
        <Zona titulo="Filas" hint="arrastrá dimensiones acá" onDrop={dropEn('filas')}>
          {filas.map((k) => <Chip key={k} item={{ key: k, label: dimLabel(k) }} tipo="dim" activa onRemove={() => mover(k, 'campos')} />)}
        </Zona>
        <Zona titulo="Columnas" hint="arrastrá dimensiones acá" onDrop={dropEn('columnas')}>
          {columnas.map((k) => <Chip key={k} item={{ key: k, label: dimLabel(k) }} tipo="dim" activa onRemove={() => mover(k, 'campos')} />)}
        </Zona>
        <Zona titulo="Valores" hint="arrastrá una medida" onDrop={dropEn('valores')}>
          <Chip item={{ key: medida, label: medLabel(medida) }} tipo="medida" activa />
        </Zona>
        <div style={{ minWidth: 150 }}>
          <div style={{ ...sx.cardTitle, marginBottom: 4 }}>Ajuste</div>
          <select value={ajuste} onChange={(e) => setAjuste(e.target.value)} style={{ ...st.input, width: '100%' }}>
            {opts.ajustes.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </div>
      </div>

      {/* Fecha */}
      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.xl }}>
        {PRESETS.map((p) => <button key={p.k} style={st.presetBtn(preset === p.k)} onClick={() => aplicarPreset(p.k)}>{p.label}</button>)}
        <span style={{ width: 1, height: 22, background: colors.border }} />
        <input type="date" value={desde} onChange={(e) => { setPreset(''); setRango([e.target.value, hasta]) }} style={st.input} />
        <span style={{ color: colors.textMeta }}>→</span>
        <input type="date" value={hasta} onChange={(e) => { setPreset(''); setRango([desde, e.target.value]) }} style={st.input} />
        {ajuste === 'rubro' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>pesos de hoy según la inflación de compra de cada rubro</span>}
        {ajuste === 'ipc' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>pesos de hoy por IPC oficial</span>}
        {ajuste === 'dolar' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>dólares al blue de cada día</span>}
      </div>

      {loading && !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
      ) : !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.err }}>No se pudieron cargar los datos.</div>
      ) : !data.filas.length ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Sin datos en el período</div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto', maxHeight: '68vh' }}>
            <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
              <thead>
                {nc > 0 ? (
                  Array.from({ length: nc }).map((_, lvl) => (
                    <tr key={lvl}>
                      {lvl === 0 && data.meta.filas.map((fd, i) => (
                        <th key={fd.key} rowSpan={nc} style={{ ...st.th, textAlign: 'left', position: 'sticky', left: i === 0 ? 0 : undefined, top: 0, zIndex: 3 }}>{fd.label}</th>
                      ))}
                      {!nf && lvl === 0 && <th rowSpan={nc} style={{ ...st.th, textAlign: 'left', position: 'sticky', top: 0 }} />}
                      {gruposNivel(lvl).map((g, gi) => (
                        <th key={gi} colSpan={g.span} style={{ ...st.thc, top: 0, position: 'sticky', borderBottom: lvl === nc - 1 ? `2px solid ${colors.border}` : st.thc.borderBottom }}>{g.label}</th>
                      ))}
                      {lvl === 0 && <th rowSpan={nc} style={{ ...st.th, top: 0, position: 'sticky', borderLeft: `1px solid ${colors.border}` }}>Total</th>}
                    </tr>
                  ))
                ) : (
                  <tr>
                    {data.meta.filas.map((fd, i) => <th key={fd.key} style={{ ...st.th, textAlign: 'left', position: 'sticky', left: i === 0 ? 0 : undefined, top: 0, zIndex: 3 }}>{fd.label}</th>)}
                    <th style={{ ...st.th, top: 0, position: 'sticky' }}>{medLabel(medida)}</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {data.filas.map((row, ri) => {
                  const prev = ri > 0 ? data.filas[ri - 1].vals : []
                  return (
                    <tr key={row.key}>
                      {row.vals.map((v, d) => {
                        const cambia = row.vals.slice(0, d + 1).join(SEP) !== prev.slice(0, d + 1).join(SEP)
                        return <td key={d} style={{ ...st.tdh, position: d === 0 ? 'sticky' : undefined, left: d === 0 ? 0 : undefined, fontWeight: d === 0 ? 600 : 400, color: d === 0 ? colors.text : colors.textSec, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={v}>{cambia ? v : ''}</td>
                      })}
                      {!nf && <td style={{ ...st.tdh, position: 'sticky', left: 0, fontWeight: 600 }}>Total</td>}
                      {nc > 0
                        ? cols.map((c) => <td key={c.key} style={{ ...st.td, background: shade(row.celdas[c.key]) }}>{row.celdas[c.key] ? fmt(row.celdas[c.key]) : '·'}</td>)
                        : <td style={{ ...st.td, background: shade(row.total) }}>{fmt(row.total)}</td>}
                      {nc > 0 && <td style={{ ...st.td, fontWeight: 600, borderLeft: `1px solid ${colors.border}` }}>{fmt(row.total)}</td>}
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={Math.max(nf, 1)} style={{ ...st.td, textAlign: 'left', fontWeight: 700, fontFamily: font.sans, borderTop: `2px solid ${colors.border}`, position: 'sticky', left: 0, background: colors.bgSoft }}>Total</td>
                  {nc > 0
                    ? cols.map((c) => <td key={c.key} style={{ ...st.td, fontWeight: 700, borderTop: `2px solid ${colors.border}`, background: colors.bgSoft }}>{fmt(c.total)}</td>)
                    : <td style={{ ...st.td, fontWeight: 700, borderTop: `2px solid ${colors.border}`, background: colors.bgSoft }}>{fmt(data.total)}</td>}
                  {nc > 0 && <td style={{ ...st.td, fontWeight: 700, borderTop: `2px solid ${colors.border}`, borderLeft: `1px solid ${colors.border}`, background: colors.bgSoft }}>{fmt(data.total)}</td>}
                </tr>
              </tfoot>
            </table>
          </div>
          {(data.truncado?.filas || data.truncado?.columnas) && (
            <div style={{ fontSize: text.meta, color: colors.warn, marginTop: space.sm }}>
              ⚠️ Se muestran las {data.truncado.filas ? 'primeras 200 filas' : ''}{data.truncado.filas && data.truncado.columnas ? ' y ' : ''}{data.truncado.columnas ? 'primeras 50 columnas' : ''} por volumen (ordenadas por total). Afiná el rango o las dimensiones.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
